import { useContext } from '@/compat';
import axios from 'axios';
import { onUnmounted, reactive, ref } from 'vue';
import { UploadingMethods, UploadingTypes } from '@/helpers/misc/file-upload/constants';
import { checkExtension, isFunction, isNullOrEmpty } from '@/helpers/misc';
import useAlert from '@/helpers/misc/dialog/useAlert';
import useConfirm from '@/helpers/misc/dialog/useConfirm';
import useContentTitleGuard from '@/helpers/content/useContentTitleGuard';
import generateUploadId from './generateUploadId';
import { getFileHash, isVideo } from "@/helpers/misc/file-upload/index";

const useFileUpload = ({ uploadUrl, method, type } = {}) => {
  const { $i18n, $lang } = useContext();
  const askUserToCancelUpload = useConfirm().warning();
  const alert = useAlert();
  const { renameForUpload } = useContentTitleGuard();

  const uploadServer = ref(uploadUrl || process.env.VUE_APP_PIRI_UPLOAD_URL);
  const fileHashLookupUrl  = ref(process.env.VUE_APP_PIRI_FILE_HASH_LOOKUP_URL ||
  "https://storage.piri.net/api/v1/upload/get-file-info-from-hash")
  const uploadMethod = ref(method || (UploadingMethods.File + UploadingMethods.Cloud));
  const uploadType = ref(type || UploadingTypes.Any);
  const requests = ref([]);

  const transformResult = (file, uploadResult) => ({
    data: uploadResult.data,
    file,
    size: file.size,
    type: file.type,
  });

  const cancelAll = async (immediate = false) => {
    if (immediate || await askUserToCancelUpload(
      $i18n.t($lang.app.WARNING),
      $i18n.t($lang.file.ARE_YOU_SURE_YOU_WANT_TO_CANCEL_UPLOAD_IN_PROGRESS),
    )) {
      requests.value.forEach(request => request.cancel());

      return true;
    }

    return false;
  };

  const getUploadedFileInfoByHash = async file => {
    try {
      if (isNullOrEmpty(file?.name) || !isVideo(file.name)){
        return null;
      }

      const fileHash = await getFileHash(file);

      return await axios.get(fileHashLookupUrl.value + "?fileHash=" + fileHash);
    } catch (e) {
      console.error("getUploadedFileInfoByHash error", e);

      return null;
    }
  }

  const uploadSingleFile = ({
    file, request, cancelToken, onUploadCompleted, convert = true
  }) => {
    getUploadedFileInfoByHash(file).then(result => {
      if (result){
        onUploadCompleted(transformResult(file, result), request)

        return
      } else {
        const formData = new window.FormData();
        formData.append('method', isFunction(uploadMethod.value) ? uploadMethod.value(file) : uploadMethod.value);
        formData.append('type', uploadType.value);
        formData.append('file', file);
        formData.append('convert', convert);

        axios.post(uploadServer.value, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          cancelToken,
          onUploadProgress(progressEvent) {
            request.progress = Number(Math.round((progressEvent.loaded / progressEvent.total) * 100));
          },
        })
          .then(uploadResult => onUploadCompleted(transformResult(file, uploadResult), request))

          .catch(error => {
            if (error.message) {
              console.error(error.message);
              request.isUploading = false;
              alert.error($i18n.t($lang.app.ERROR), $i18n.t($lang.app.UPLOAD_FAILED));
            } else {
              console.info('File upload request canceled.');
            }
          });

        return
      }
    })
  };

  const validateExtension = (file, extensions) => {
    if (!file) {
      alert.error($i18n.t($lang.app.ERROR), $i18n.t($lang.file.FILE_NOT_EXIST));

      return false;
    }

    if (extensions && !checkExtension(extensions, file.name)) {
      alert.error(
        $i18n.t($lang.app.ERROR),
        $i18n.t($lang.file.FILE_FORMAT_YOU_SELECTED_IS_NOT_SUITABLE).replace('{0}', extensions),
      );

      return false;
    }

    return true;
  };

  /**
   * Dosya adinda baslik sonrasina eklenecek eki cozer.
   * fileNameSuffix verilmediyse ve ayni anda birden fazla dosya yukleniyorsa sira numarasi kullanilir.
   */
  const resolveFileNameSuffix = (fileNameSuffix, file, index, total) => {
    if (isFunction(fileNameSuffix)) {
      return fileNameSuffix(file, index, total) || '';
    }

    if (fileNameSuffix) {
      return fileNameSuffix;
    }

    return total > 1 ? String(index + 1) : '';
  };

  const upload = ({
    files, onUploadCompleted, extensions, validateFile, convert=true, fileNameSuffix
  }) => {
    const filesToUpload = Array.from(files || []);
    const renamedFiles = filesToUpload.map(
      (file, index) => renameForUpload(file, resolveFileNameSuffix(fileNameSuffix, file, index, filesToUpload.length)),
    );
    const result = Promise.all(renamedFiles.map(async file => {
      if (!validateExtension(file, extensions) || !(!isFunction(validateFile) || await validateFile(file))) {
        return null;
      }

      const axiosSource = axios.CancelToken.source();
      const cancelToken = axiosSource.token;
      const id = generateUploadId();

      const request = {
        id,
        isUploading: true,
        cancel: axiosSource.cancel,
        progress: 0,
        file,
      };

      requests.value.push(reactive(request));

      if (!onUploadCompleted) {
        return new Promise(resolve => uploadSingleFile({
          file,
          request,
          cancelToken,
          onUploadCompleted: uploadResult => resolve(uploadResult),
          convert
        }));
      }

      uploadSingleFile({
        file, request, cancelToken, onUploadCompleted, convert
      });

      return request;
    }));

    return result;
  };

  onUnmounted(async () => {
    await cancelAll(true);
  });

  return {
    requests,
    transformResult,
    cancelAll,
    generateUploadId,
    validateExtension,
    upload,
  };
};

export default useFileUpload;
